import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../constants";
import { useLang } from "../i18n";

const fadeUp = {
  hidden: { opacity: 0, y: 44 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.95, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Story() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      id="story"
      ref={ref}
      data-testid="story"
      className="relative px-6 py-28 md:px-12 md:py-40"
      style={{ backgroundImage: "url(/parchment.jpg)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-[#F3E7C9]/60" />
      <div className="relative grid items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-[0.65rem] uppercase tracking-[0.5em] text-goldink"
          >
            {t("story.eyebrow")}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display text-ink mt-6 max-w-2xl text-4xl leading-tight md:text-6xl"
          >
            {t("story.title")}
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-10 max-w-2xl text-lg leading-relaxed text-ink/85"
          >
            {t("story.p1")}
          </motion.p>
          <motion.p
            variants={fadeUp}
            custom={3}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-6 max-w-2xl text-lg leading-relaxed text-ink/85"
          >
            {t("story.p2")}
          </motion.p>
          <motion.div
            variants={fadeUp}
            custom={4}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-14 grid grid-cols-3 gap-6 border-t border-gold/15 pt-8"
          >
            {t("story.stats").map((s, i) => (
              <div key={i} data-testid={`story-stat-${i}`}>
                <p className="font-display text-3xl text-goldink md:text-4xl">{s.n}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.3em] text-ink2">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <div className="absolute -inset-3 border border-gold/25 clip-frame" />
          <motion.div
            variants={fadeUp}
            custom={2}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="clip-frame relative overflow-hidden"
            style={{ boxShadow: "0 40px 90px rgba(0,0,0,0.6)" }}
          >
            <motion.img src={IMAGES.story} alt="III ROMA — the house" className="h-full w-full object-cover" style={{ y, scale: 1.12 }} />
            <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_40%,transparent,rgba(10,8,8,0.5))]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
